/* eslint-disable semi */
/* eslint-disable comma-dangle */
// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Fragment } from 'react'
import { Slide, toast } from 'react-toastify'

import baseurl from '../../../@core/auth/jwt/baseurl'
import emiter from '../../../utility/hooks/emiiter'

const showErrorMsg = (error) => {
  toast.error(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Error</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{error}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

const showSuccessMsg = (msg) => {
  toast.success(
    <Fragment>
      <div className="toastify-header">
        <div className="title-wrapper">
          <h6 className="toast-title fw-bold">Success</h6>
        </div>
      </div>
      <div className="toastify-body">
        <span>{msg}</span>
      </div>
    </Fragment>,
    {
      icon: false,
      transition: Slide,
      hideProgressBar: false,
      autoClose: 2000
    }
  )
}

export const getData = createAsyncThunk(
  'appDecisionDraft/getData',
  async (params) => {
    const response = await baseurl.get('/api/app/decision-draft', {
      params
    })
    return {
      params,
      data: response.data.items,
      total: response.data.totalCount
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const getById = createAsyncThunk(
  'appDecisionDraft/getById',
  async (id) => {
    try {
      const response = await baseurl.get(`/api/app/decision-draft/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (response.status === 200) {
        return response.data
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return rejectWithValue(response.data)
    } catch (error) {
      return rejectWithValue(error)
    }
  },
  {
    dispatchConditionRejection: true
  }
)

export const addDecisionDraft = createAsyncThunk(
  'appDecisionDraft/addDecisionDraft',
  async (decisionDraft, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.post('/api/app/decision-draft', decisionDraft, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Added Successfully')
        emiter.emit('close-decisiontype-add-or-edit-popup')
        await dispatch(getData(getState().decisionDraft.params))
        await dispatch(getById(res.data.id))
        return decisionDraft
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const updateDecisionDraft = createAsyncThunk(
  'appDecisionDraft/updateDecisionDraft',
  async (decisiontype, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.put(`/api/app/decision-draft/${decisiontype.id}`, decisiontype, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 200) {
        showSuccessMsg('Updated Successfully')
        await dispatch(getData(getState().decisionDraft.params))
        await dispatch(getById(decisiontype.id))
        return decisiontype
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteDecisionDraft = createAsyncThunk(
  'appDecisionDraft/deleteDecisionDraft',
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await baseurl.delete(`/api/app/decision-draft/${id}`, {
        validateStatus: (status) => {
          return status >= 200 && status <= 500
        }
      })
      if (res.status === 204) {
        emiter.emit('close-decisiontype-delete-popup')
        await dispatch(getData(getState().decisionDraft.params))
        return id
      } else if (res.status === 403) {
        showErrorMsg(res.data.error.message)
      }
      return null
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const initDecisionDraft = createAsyncThunk('appDecisionDraft/initDecisionDraft', (loading) => loading, {})

export const loadDecisionDrafttLookups = createAsyncThunk(
  'appDecisionDraft/loadDecisionDrafttLookups',
  async () => {
    const caseTypesResponse = await baseurl.get('/api/app/case-type', {
      params: {
        isParant: true,
        MaxResultCount: 1000
      }
    })

    return {
      caseTypes: caseTypesResponse.data?.items
    }
  },
  {}
)

export const appDecisionDraftSlice = createSlice({
  name: 'appDecisionDraft',
  initialState: {
    data: [],
    total: 1,
    params: {},
    allData: [],
    selectedDecisionDraft: null,
    loading: true,
    getByIdLoading: false,
    saveLoading: false
  },
  reducers: {
    setUploads: (state, action) => {
      state.selectedDecisionDraft = {
        ...state.selectedDecisionDraft,
        uploads: action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getData.pending, (state) => {
        state.loading = true
      })
      .addCase(getData.rejected, (state) => {
        state.loading = false
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.total
        state.loading = false
      })

      .addCase(getById.pending, (state) => {
        state.getByIdLoading = true
        state.selectedDecisionDraft = null
      })
      .addCase(getById.rejected, (state) => {
        state.getByIdLoading = false
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.getByIdLoading = false
        state.selectedDecisionDraft = action.payload
      })

      .addCase(addDecisionDraft.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(addDecisionDraft.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(addDecisionDraft.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(updateDecisionDraft.pending, (state) => {
        state.saveLoading = true
      })
      .addCase(updateDecisionDraft.rejected, (state) => {
        state.saveLoading = false
      })
      .addCase(updateDecisionDraft.fulfilled, (state) => {
        state.saveLoading = false
      })

      .addCase(deleteDecisionDraft.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteDecisionDraft.rejected, (state) => {
        state.deleteLoading = false
      })
      .addCase(deleteDecisionDraft.fulfilled, (state) => {
        state.deleteLoading = false
      })
      .addCase(initDecisionDraft.fulfilled, (state) => {
        state.selectedDecisionDraft = null
      })

    builder.addCase(loadDecisionDrafttLookups.fulfilled, (state, action) => {
      state.caseTypes = action.payload.caseTypes
    })
  }
})
export const { setUploads } = appDecisionDraftSlice.actions
export default appDecisionDraftSlice.reducer